import { useCallback, useId, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import artifactViewersCopy from '@/i18n/ns/artifact-viewers';
import { DeckStage } from '../../deck/stage';
import { Presenter } from '../../deck/present';
import { filmstripTarget } from '../../deck/filmstrip';
import { WorkspaceIcon } from '../../icons';
import type { SavedDeckDisplay } from '../../../../src/artifacts/deck-display.mjs';

/** Same slide geometry and presenter as retained decks, with real canonical
 * slide identities. The owner supplies the selected C9 view and export action. */
export function SavedDeckDisplayView({ display }: { display: SavedDeckDisplay; locale?: string }) {
  const copy = useNamespaceCopy(artifactViewersCopy);
  const prefix = useId();
  const [at, setAt] = useState(0);
  const [presenting, setPresenting] = useState(false);
  const count = display.slides.length;
  const shown = Math.min(at, count - 1);
  const current = display.slides[shown];
  const step = useCallback(
    (by: number) => {
      setAt((was) => Math.min(count - 1, Math.max(0, Math.min(was, count - 1) + by)));
    },
    [count],
  );
  const stopPresenting = useCallback(() => setPresenting(false), []);
  const label = formatCopy(copy.slideOf, { at: shown + 1, count });
  return (
    <section
      className="ws-deck"
      data-kind="deck"
      data-inspect-node={display.id}
      aria-label={display.title}
    >
      <div className="ws-deck-bar">
        <button
          className="ws-button"
          onClick={() => setPresenting(true)}
          aria-label={copy.presenting}
        >
          <WorkspaceIcon name="play" size={14} />
          {copy.presenting}
        </button>
        <span className="position" role="status">
          {label}
        </span>
      </div>
      <div
        id={`${prefix}-stage`}
        role="tabpanel"
        aria-labelledby={`${prefix}-slide-${shown}`}
        data-inspect-node={current.id}
      >
        <DeckStage slide={current.content} label={label} />
      </div>
      <div
        className="ws-filmstrip"
        role="tablist"
        aria-label={display.title}
        onKeyDown={(event) => {
          if (presenting) return;
          const to = filmstripTarget(event.key, shown, count);
          if (to === null) return;
          event.preventDefault();
          setAt(to);
          event.currentTarget
            .querySelector<HTMLButtonElement>(`[data-slide-index="${to}"]`)
            ?.focus();
        }}
      >
        {display.slides.map((slide, index) => (
          <button
            key={slide.id}
            id={`${prefix}-slide-${index}`}
            data-slide-index={index}
            data-slide-id={slide.id}
            role="tab"
            aria-selected={index === shown}
            aria-controls={`${prefix}-stage`}
            tabIndex={index === shown ? 0 : -1}
            aria-label={formatCopy(copy.slideOf, { at: index + 1, count })}
            onClick={() => setAt(index)}
          >
            <DeckStage slide={slide.content} />
            <span className="number">{index + 1}</span>
          </button>
        ))}
      </div>
      {current.content.notes && (
        <div className="ws-deck-notes">
          <strong>{copy.showNotes}</strong>
          {current.content.notes}
        </div>
      )}
      {presenting && (
        <Presenter
          title={display.title}
          slide={current.content}
          at={shown}
          count={count}
          onStep={step}
          onClose={stopPresenting}
        />
      )}
    </section>
  );
}
